import axios from "axios";
import { toast } from "sonner";
import { supabase } from "@/lib/supabaseClient";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

// Resolve backend-served images. Stored file URLs are relative (`/api/files/...`)
// so they render in any environment; prepend the backend origin at render time.
export function resolveImg(url) {
  if (!url || typeof url !== "string") return url || "";
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
}

api.interceptors.request.use(async (config) => {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Friendly notice when a public endpoint is rate-limited.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 429) {
      toast.error("You're going a little fast — please wait a moment and try again.");
    }
    return Promise.reject(err);
  }
);

// Poll a background AI job (started via an `.../ai-.../start` endpoint) until it
// finishes. Resolves with the job result, or throws an axios-shaped error so callers
// can keep using formatApiError(e.response?.data?.detail).
export async function pollAiJob(jobId, { interval = 2500, timeoutMs = 180000 } = {}) {
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    const { data } = await api.get(`/assessment-jobs/${jobId}`);
    if (data.status === "done") return data.result;
    if (data.status === "error") {
      throw { response: { status: data.code || 502, data: { detail: data.detail || "The AI request failed. Please try again." } } };
    }
    await new Promise((r) => setTimeout(r, interval));
  }
  throw { response: { status: 504, data: { detail: "The AI is taking longer than expected. Please try again." } } };
}

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
